import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChainSwitcher } from "./ChainSwitcher";
import { ThemeToggle } from "./ThemeToggle";
import { WalletButton } from "./WalletButton";
import { useAuthStore } from "@/stores/auth";
import { cn } from "@/lib/cn";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/create", label: "Create" },
  { to: "/pfp", label: "PFP Studio" },
  { to: "/mint", label: "1/1 Mint" },
  { to: "/dashboard", label: "Dashboard" },
];

export function Topbar() {
  const isAuthed = useAuthStore((s) => s.isAuthed());
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu whenever the route changes.
  const closeMenu = () => setMenuOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between gap-2 px-4 sm:h-16 sm:gap-4">
        <Link to="/" className="text-base font-bold tracking-tight sm:text-lg">
          Atelier
        </Link>
        <nav className="hidden items-center gap-1 md:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                cn(
                  "rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                  isActive && "bg-muted text-foreground",
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className="hidden md:flex md:items-center md:gap-2">
            <ChainSwitcher />
            <ThemeToggle />
          </div>
          <WalletButton />
          {isAuthed && (
            <span className="hidden text-xs text-muted-foreground lg:inline">signed in</span>
          )}
          <button
            type="button"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className="ml-1 inline-flex h-9 w-9 items-center justify-center rounded-md border border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground md:hidden"
          >
            {menuOpen ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="border-t border-border bg-background md:hidden">
          <ul className="mx-auto flex max-w-6xl flex-col px-2 py-2">
            {NAV_LINKS.map((l) => {
              const isActive =
                l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to);
              return (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    onClick={closeMenu}
                    className={cn(
                      "block rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
                      isActive && "bg-muted text-foreground",
                    )}
                  >
                    {l.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 border-t border-border px-4 py-3">
            <ChainSwitcher />
            <ThemeToggle />
          </div>
        </nav>
      )}
    </header>
  );
}
